import { useState, useEffect, useRef } from "react";
import { X, Mail, Check } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const STORAGE_KEY_SUBSCRIBED = "eliora_newsletter_subscribed";
const STORAGE_KEY_DISMISSED = "eliora_newsletter_dismissed_until";
const STORAGE_KEY_SCROLL_TRACKED = "eliora_blog_scroll60_tracked";
const DISMISS_DAYS = 14;

/**
 * Shows once per visitor after they scroll past ~60% of a blog post.
 * - Skips if already subscribed (persisted forever)
 * - Skips if dismissed within last 14 days
 * - Also emits a one-time `blog_scroll_60` GA4 event per session
 */
export default function NewsletterPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const triggeredRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Skip if already subscribed
    if (localStorage.getItem(STORAGE_KEY_SUBSCRIBED) === "1") return;
    // Skip if dismissed recently
    const dismissedUntil = parseInt(localStorage.getItem(STORAGE_KEY_DISMISSED) || "0", 10);
    if (dismissedUntil && Date.now() < dismissedUntil) return;

    const onScroll = () => {
      if (triggeredRef.current) return;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const pct = scrollTop / docHeight;
      if (pct >= 0.6) {
        triggeredRef.current = true;
        // Track scroll 60% once per session
        if (!sessionStorage.getItem(STORAGE_KEY_SCROLL_TRACKED)) {
          sessionStorage.setItem(STORAGE_KEY_SCROLL_TRACKED, "1");
          trackEvent("blog_scroll_60", {
            event_category: "engagement",
            page_path: window.location.pathname,
          });
        }
        // Open after a brief delay so it doesn't feel jarring
        setTimeout(() => setOpen(true), 500);
        window.removeEventListener("scroll", onScroll);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => {
    setOpen(false);
    if (!success) {
      const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem(STORAGE_KEY_DISMISSED, String(until));
      trackEvent("newsletter_dismissed", {
        event_category: "engagement",
        page_path: window.location.pathname,
      });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          name: name.trim(),
          source: "blog_popup",
          language: "es",
        }),
      });
      if (!res.ok) throw new Error("subscribe_failed");
      setSuccess(true);
      localStorage.setItem(STORAGE_KEY_SUBSCRIBED, "1");
      trackEvent("newsletter_subscribed", {
        event_category: "conversion",
        page_path: window.location.pathname,
      });
    } catch (err) {
      console.error(err);
      // fail silently — show a generic message via the success UI? keep input and let user retry
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="newsletter-popup"
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 sm:p-6 bg-[#1a2421]/55 backdrop-blur-sm animate-in fade-in"
    >
      <div className="relative w-full max-w-md bg-[#f9f6f0] rounded-3xl shadow-2xl overflow-hidden">
        <button
          onClick={close}
          aria-label="Cerrar"
          data-testid="newsletter-close-btn"
          className="absolute top-3 right-3 p-2 rounded-full bg-white/70 hover:bg-white text-[#1a2421] z-10"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="h-1.5 bg-gradient-to-r from-[#bc6c25] via-[#e0a06b] to-[#bc6c25]" />

        <div className="p-7 md:p-8">
          {!success ? (
            <>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#bc6c25]/10 text-[#bc6c25] text-[10px] uppercase tracking-[0.22em] font-semibold">
                <Mail className="w-3 h-3" /> Carta mensual
              </div>
              <h3 className="font-serif text-2xl md:text-3xl text-[#1a2421] mt-3 leading-tight">
                Una vez al mes, en tu correo.
              </h3>
              <p className="text-sm text-[#5c6b61] mt-2 leading-relaxed">
                Ciencia, longevidad y un protocolo personal que escribo yo (Sarai). Sin spam.
                Si no te resuena, te das de baja en un clic.
              </p>

              <form onSubmit={submit} className="mt-5 space-y-3">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Tu nombre (opcional)"
                  data-testid="newsletter-name-input"
                  className="w-full px-4 py-3 rounded-xl bg-white border border-[#2c4a3b]/15 focus:outline-none focus:border-[#bc6c25] text-sm"
                />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  data-testid="newsletter-email-input"
                  className="w-full px-4 py-3 rounded-xl bg-white border border-[#2c4a3b]/15 focus:outline-none focus:border-[#bc6c25] text-sm"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="newsletter-submit-btn"
                  className="w-full py-3 rounded-xl bg-[#2c4a3b] hover:bg-[#1f3529] disabled:opacity-50 text-white text-sm font-semibold tracking-wide transition-colors"
                >
                  {submitting ? "Suscribiendo..." : "Quiero recibirla"}
                </button>
              </form>

              <p className="text-[11px] text-[#5c6b61] mt-3 leading-relaxed">
                Al suscribirte aceptas recibir correos de Eliora Longevity. Privacidad respetada.
              </p>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-14 h-14 mx-auto rounded-full bg-[#2c4a3b]/10 grid place-items-center">
                <Check className="w-7 h-7 text-[#2c4a3b]" />
              </div>
              <h3 className="font-serif text-2xl text-[#1a2421] mt-4">¡Listo!</h3>
              <p className="text-sm text-[#5c6b61] mt-2">
                Te escribiré pronto. Mientras tanto, sigue explorando los artículos.
              </p>
              <button
                onClick={close}
                className="mt-5 px-6 py-2.5 rounded-full bg-[#bc6c25] hover:bg-[#9c581e] text-white text-sm font-semibold"
                data-testid="newsletter-done-btn"
              >
                Seguir leyendo
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
